/**
 * User service for profile management operations.
 *
 * Provides methods for updating user profile information including language preference.
 */

import { api } from './api';
import { invalidateShared, SHARED_KEYS } from './sharedResource';

// Denormalize language codes from frontend to backend format
function denormalizeLanguageCode(lang: string): string {
  const languageMap: Record<string, string> = {
    'pt-BR': 'pt',
    pt_br: 'pt',
    es: 'es',
    en: 'en',
  };
  return languageMap[lang] || lang;
}

export interface UpdateProfileRequest {
  full_name: string;
  timezone: string;
  language: string;
}

export interface UpdateProfileResponse {
  message: string;
  user: {
    id: string;
    email: string;
    full_name: string;
    email_verified: boolean;
  };
}

/**
 * User service for profile management.
 */
export const userService = {
  /**
   * Updates the user's profile including language preference.
   *
   * @param data - Profile update data
   * @returns Promise resolving to update confirmation
   *
   * @example
   * ```typescript
   * await userService.updateProfile({
   *   full_name: 'John Doe',
   *   timezone: 'UTC',
   *   language: 'pt-BR'
   * });
   * ```
   */
  async updateProfile(data: UpdateProfileRequest): Promise<UpdateProfileResponse> {
    const response = await api.put('/auth/profile', data);
    // Drop the shared profile cache so banners/hooks don't serve the
    // pre-update profile for the rest of the TTL.
    invalidateShared(SHARED_KEYS.profile);
    return response.data;
  },

  /**
   * Updates only the user's language preference.
   *
   * @param language - Language code (e.g., 'en', 'pt-BR', 'es')
   * @returns Promise resolving to update confirmation
   *
   * @example
   * ```typescript
   * await userService.updateLanguage('pt-BR');
   * ```
   */
  async updateLanguage(language: string): Promise<UpdateProfileResponse> {
    // First get current profile to preserve other fields
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('User not authenticated');
    }

    const profileResponse = await api.get('/auth/profile', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const currentUser = profileResponse.data.user;

    // Update profile with new language, preserving other fields
    // Denormalize frontend language code to backend format
    return this.updateProfile({
      full_name: currentUser.full_name,
      timezone: currentUser.timezone || 'UTC',
      language: denormalizeLanguageCode(language),
    });
  },
};
